import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '../../context/AuthContext.jsx';
import { PERMISSIONS } from '../../lib/permissions';
import supabase from '../../lib/supabaseClient.js';
import KnowledgeBase from './Knowledgebase.jsx';
import InstructionRebuildTool from '../InstructionRebuildTool';
import ABTestingDashboard from '../ABTestingDashboard';
import LearningAnalytics from '../LearningAnalytics';
import { useLearningInsights } from '../../pages/AIStudio/hooks/useLearningInsights.js';
import { useMetricsPolling } from '../../pages/AIControlRoomAdaptive/hooks/useMetricsPolling.js';
import {
  Brain,
  FileText,
  RefreshCw,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  Clock,
  TrendingUp,
  TrendingDown,
  MessageSquare,
  User,
  Target,
  Plus,
  Trash2,
  X,
  Save,
  Sparkles,
  Zap,
  Flame,
  Check,
  Lightbulb,
  ThumbsUp,
  Bot,
  Activity,
  ArrowRight,
  Eye,
  Settings,
  FlaskConical,
  GraduationCap,
  Flag
} from 'lucide-react';
import {
  toneOptions,
  initialPersonaOptions,
  engagementPersonaOptions,
  followupPersonaOptions,
  strategyTemplates
} from './aiconfigurationhub/constants';
import {
  buildInstructionBundle,
  buildInitialInstruction,
  buildFollowupInstruction
} from '../../lib/instructionBuilder.js';

// ============================================
// AI STUDIO - Intelligence First + Full Config
// ============================================

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  if (process.env.NODE_ENV === 'development') return 'http://localhost:3001';
  return 'https://api.getsurfox.com/api';
};

const callAPI = async (endpoint, options = {}) => {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Authentication required');

  const response = await fetch(`${getApiBaseUrl()}${endpoint}`, {
    ...options,
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
      ...options.headers,
    },
  });

  if (!response.ok) {
    const errorText = await response.text();
    let errorData;
    try { errorData = JSON.parse(errorText); } catch { errorData = { error: `Server error (${response.status})` }; }
    throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
  }

  const text = await response.text();
  return text ? JSON.parse(text) : {};
};

// ============================================
// AGENT CARD - Intelligence First
// ============================================
const AgentCard = ({ 
  campaign, 
  tenantId,
  isExpanded, 
  onToggle,
  onOpenCustomize
}) => {
  // Fetch real metrics for this campaign
  const { data: metrics, isLoading: metricsLoading } = useMetricsPolling({
    tenantId: tenantId,
    campaignId: campaign.id,
    dateRange: 'last7',
    enabled: !!campaign.id && !!tenantId
  });

  // Fetch learning insights for this campaign
  const { data: learningInsights, isLoading: insightsLoading } = useLearningInsights(campaign.id);

  // Intelligence data from real metrics
const intelligence = useMemo(() => ({
  responseRate: metrics?.responseRate || 0,
  responseRateTrend: metrics?.responseTrend || 0,
  avgTimeToHot: metrics?.avgTimeToHotDisplay || '0h',
  hotLeadsThisWeek: metrics?.totalHotLeads || 0,
  conversationsActive: metrics?.activeConversations || 0,
  optOutRate: metrics?.optOutRate || 0,
  lastActivity: '–',
  // Real learning data
  topPerformingPhrase: learningInsights?.topOpener 
    ? `${learningInsights.topOpener.charAt(0).toUpperCase() + learningInsights.topOpener.slice(1)} openers` 
    : null,
  topOpenerResponsePct: learningInsights?.topOpenerResponsePct || 0,
  tonePerformance: learningInsights?.tonePerformance || 0,
  topTone: learningInsights?.topTone || null,
  hasLearningData: learningInsights?.hasData || false,
  totalSamples: learningInsights?.totalSamples || 0,
  metricsLoading,
  insightsLoading,
  suggestion: {
    show: false,
    title: 'Extend follow-up window',
    description: 'Leads in your industry respond 23% better with Day 5 instead of Day 3',
    impact: '+23% engagement'
  }
}), [metrics, metricsLoading, learningInsights, insightsLoading]);

  const currentBehavior = useMemo(() => ({
    tone: campaign.engagement_tone || 'Friendly & Casual',
    persona: campaign.engagement_persona || 'Closer',
    sequence: [
      campaign.followup_delay_1 || 3,
      campaign.followup_delay_2 || 7,
      campaign.followup_delay_3 || 14
    ],
    aiName: campaign.ai_representative_name || 'Alex'
  }), [campaign]);

  const isLive = campaign.ai_on;
  const isHealthy = intelligence.responseRate > 25 && intelligence.optOutRate < 5;

  return (
    <motion.div
      data-tour="agent-card"
      layout
      className={`bg-white rounded-xl border overflow-hidden transition-all duration-300 ${
        isExpanded 
          ? 'border-orange-200 shadow-lg' 
          : 'border-slate-200 hover:border-slate-300 hover:shadow-md'
      }`}
    >
      {/* Collapsed Header */}
      <button 
  onClick={() => {
    onToggle();
    if (!isExpanded) {
      window.dispatchEvent(new CustomEvent('tour-agent-expanded'));
    }
  }} 
  className="w-full text-left"
>
        <div className="p-4 sm:p-5">
          <div className="flex items-center justify-between gap-3">
            {/* Left: Identity */}
            <div className="flex items-center space-x-3 sm:space-x-4 min-w-0 flex-1">
              <div className="relative flex-shrink-0">
                <div className={`w-10 h-10 sm:w-12 sm:h-12 rounded-xl flex items-center justify-center ${
                  isLive ? 'bg-orange-500' : 'bg-slate-200'
                }`}>
                  <Bot className={`w-5 h-5 sm:w-6 sm:h-6 ${isLive ? 'text-white' : 'text-slate-400'}`} />
                </div>
                {isLive && (
                  <span className="absolute -top-1 -right-1 flex h-3 w-3">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-3 w-3 bg-emerald-500"></span>
                  </span>
                )}
              </div>
              
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <h3 className="font-semibold text-slate-900 text-sm sm:text-base truncate">{campaign.name}</h3>
                  {isLive && (
                    <span className="px-1.5 sm:px-2 py-0.5 bg-emerald-50 text-emerald-700 text-[10px] sm:text-xs font-medium rounded-full flex-shrink-0">Live</span>
                  )}
                </div>
                <p className="text-xs sm:text-sm text-slate-500 truncate">{currentBehavior.tone} · {currentBehavior.persona}</p>
              </div>
            </div>

            {/* Right: Metrics */}
            <div className="flex items-center gap-2 sm:gap-6 flex-shrink-0">
<div className="text-right hidden sm:block">
  <div className="flex items-center justify-end space-x-1">
    <span className="text-lg font-bold text-slate-900">{intelligence.responseRate}%</span>
    <span className={`text-xs font-medium flex items-center ${intelligence.responseRateTrend > 0 ? 'text-emerald-500' : 'text-red-500'}`}>
      {intelligence.responseRateTrend > 0 ? <TrendingUp className="w-3 h-3" /> : <TrendingDown className="w-3 h-3" />}
      {Math.abs(intelligence.responseRateTrend)}%
    </span>
  </div>
  <p className="text-xs text-slate-400">response rate · last 7 days</p>
</div>

              <div className="text-right hidden md:block">
                <div className="flex items-center justify-end space-x-1">
                  <Flame className="w-4 h-4 text-orange-500" />
                  <span className="text-lg font-bold text-slate-900">{intelligence.hotLeadsThisWeek}</span>
                </div>
                <p className="text-xs text-slate-400">hot this week</p>
              </div>

              <div className={`w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center ${isHealthy ? 'bg-emerald-50' : 'bg-amber-50'}`}>
                {isHealthy ? <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-500" /> : <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-amber-500" />}
              </div>

              <motion.div animate={{ rotate: isExpanded ? 180 : 0 }} transition={{ duration: 0.2 }}>
                <ChevronDown className="w-4 h-4 sm:w-5 sm:h-5 text-slate-400" />
              </motion.div>
            </div>
          </div>
        </div>
      </button>

      {/* Expanded: Intelligence Feed */}
      <AnimatePresence>
        {isExpanded && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.3, ease: 'easeInOut' }}
            className="overflow-hidden"
          >
            <div className="border-t border-slate-100">
              
              {/* Right Now */}
              <div className="px-4 sm:px-5 py-4 bg-slate-50/50">
                <div className="flex items-center space-x-2 mb-3">
                  <Activity className="w-4 h-4 text-slate-400" />
                  <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Right Now</span>
                  <span className="text-xs text-slate-400">· {intelligence.lastActivity}</span>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-2 sm:gap-3">
                  <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-slate-100">
                    <div className="text-xl sm:text-2xl font-bold text-slate-900">{intelligence.conversationsActive}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">active convos</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-slate-100">
                    <div className="text-xl sm:text-2xl font-bold text-orange-500">{intelligence.hotLeadsThisWeek}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">hot this week</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-slate-100">
                    <div className="text-xl sm:text-2xl font-bold text-slate-900">{intelligence.avgTimeToHot}</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">avg to hot</div>
                  </div>
                  <div className="bg-white rounded-lg p-2.5 sm:p-3 border border-slate-100">
                    <div className="text-xl sm:text-2xl font-bold text-slate-900">{intelligence.optOutRate}%</div>
                    <div className="text-[10px] sm:text-xs text-slate-500">opt-out</div>
                  </div>
                </div>
              </div>

              {/* What's Working */}
<div className="px-4 sm:px-5 py-4 border-t border-slate-100">
  <div className="flex items-center space-x-2 mb-3">
    <ThumbsUp className="w-4 h-4 text-emerald-500" />
    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">What's Working</span>
    {intelligence.hasLearningData && (
      <span className="text-[10px] text-slate-400">· {intelligence.totalSamples} samples</span>
    )}
  </div>
  
  {!intelligence.hasLearningData ? (
    <div className="p-4 bg-slate-50 rounded-lg border border-slate-100 text-center">
      <p className="text-xs sm:text-sm text-slate-500">Not enough data yet. Insights will appear after more conversations.</p>
    </div>
  ) : (
    <div className="space-y-2">
      <div className="flex items-start space-x-3 p-2.5 sm:p-3 bg-emerald-50/50 rounded-lg border border-emerald-100">
        <div className="w-5 h-5 sm:w-6 sm:h-6 bg-emerald-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
          <Check className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-emerald-600" />
        </div>
        <div className="min-w-0 flex-1">
<p className="text-xs sm:text-sm text-slate-700">
  {intelligence.tonePerformance > 0 ? (
    <>
      <span className="font-medium capitalize">{intelligence.topTone}</span> tone is outperforming by 
      <span className="font-semibold text-emerald-600"> +{intelligence.tonePerformance}%</span>
    </>
  ) : (
    <>
      <span className="font-medium capitalize">{intelligence.topTone}</span> tone performing best
    </>
  )}
</p>
<p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">
  {intelligence.tonePerformance > 0 ? 'vs. other approaches' : `${intelligence.totalSamples} conversations analyzed`}
</p>
        </div>
      </div>
      
      {intelligence.topPerformingPhrase && (
        <div className="flex items-start space-x-3 p-2.5 sm:p-3 bg-slate-50 rounded-lg border border-slate-100">
          <div className="w-5 h-5 sm:w-6 sm:h-6 bg-slate-100 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
            <MessageSquare className="w-2.5 h-2.5 sm:w-3 sm:h-3 text-slate-500" />
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-xs sm:text-sm text-slate-700">
              Top opener: <span className="font-medium">"{intelligence.topPerformingPhrase}"</span>
            </p>
            <p className="text-[10px] sm:text-xs text-slate-500 mt-0.5">{intelligence.topOpenerResponsePct}% response rate</p>
          </div>
        </div>
      )}
    </div>
  )}
</div>

              {/* AI Suggestion */}
              {intelligence.suggestion.show && (
                <div className="px-4 sm:px-5 py-4 border-t border-slate-100">
                  <div className="flex items-center space-x-2 mb-3">
                    <Lightbulb className="w-4 h-4 text-amber-500" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Suggestion</span>
                  </div>
                  
                  <div className="bg-gradient-to-r from-amber-50 to-orange-50 rounded-xl p-3 sm:p-4 border border-amber-200/50">
                    <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-slate-900 text-sm sm:text-base">{intelligence.suggestion.title}</h4>
                        <p className="text-xs sm:text-sm text-slate-600 mt-1">{intelligence.suggestion.description}</p>
                        <div className="flex items-center space-x-2 mt-2">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] sm:text-xs font-medium">
                            <TrendingUp className="w-3 h-3 mr-1" />
                            {intelligence.suggestion.impact}
                          </span>
                        </div>
                      </div>
                      <button className="w-full sm:w-auto sm:ml-4 px-4 py-2 bg-orange-500 text-white text-sm font-medium rounded-lg hover:bg-orange-600 transition-colors flex items-center justify-center space-x-1">
                        <span>Apply</span>
                        <ArrowRight className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </div>
              )}

              {/* Current Behavior */}
              <div className="px-4 sm:px-5 py-4 border-t border-slate-100 bg-slate-50/30">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Eye className="w-4 h-4 text-slate-400" />
                    <span className="text-xs font-semibold text-slate-500 uppercase tracking-wide">Current Behavior</span>
                  </div>
                    <button
                      onClick={(e) => { e.stopPropagation(); onOpenCustomize(); }}
                      data-tour="customize-agent"
                      className="text-xs text-orange-600 hover:text-orange-700 font-medium flex items-center space-x-1"
                    >
                    <Settings className="w-3 h-3" />
                    <span>Customize</span>
                  </button>
                </div>

                <div className="flex flex-wrap gap-1.5 sm:gap-2">
                  <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-white rounded-lg border border-slate-200 text-xs sm:text-sm">
                    <User className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mr-1.5 sm:mr-2" />
                    <span className="text-slate-600">Rep:</span>
                    <span className="font-medium text-slate-800 ml-1">{currentBehavior.aiName}</span>
                  </div>
                  <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-white rounded-lg border border-slate-200 text-xs sm:text-sm">
                    <MessageSquare className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mr-1.5 sm:mr-2" />
                    <span className="text-slate-600">Style:</span>
                    <span className="font-medium text-slate-800 ml-1 truncate max-w-[80px] sm:max-w-none">{currentBehavior.tone}</span>
                  </div>
                  <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-white rounded-lg border border-slate-200 text-xs sm:text-sm">
                    <Target className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mr-1.5 sm:mr-2" />
                    <span className="text-slate-600">Approach:</span>
                    <span className="font-medium text-slate-800 ml-1">{currentBehavior.persona}</span>
                  </div>
                  <div className="inline-flex items-center px-2 sm:px-3 py-1 sm:py-1.5 bg-white rounded-lg border border-slate-200 text-xs sm:text-sm">
                    <Clock className="w-3 h-3 sm:w-3.5 sm:h-3.5 text-slate-400 mr-1.5 sm:mr-2" />
                    <span className="text-slate-600">Follow-ups:</span>
                    <span className="font-medium text-slate-800 ml-1">Day {currentBehavior.sequence.join(', ')}</span>
                  </div>
                </div>
              </div>

            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

// ============================================
// CUSTOMIZE MODAL - Full Configuration
// ============================================
const CustomizeModal = ({
  isOpen,
  onClose,
  campaign,
  strategyConfig,
  onStrategyChange,
  onFollowupChange,
  onAddQuestion,
  onUpdateQuestion,
  onRemoveQuestion,
  onApplyTemplate,
  onSave,
  saving,
  unsavedChanges,
  canEditInstructions,
  canRebuildBundle
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-start justify-center min-h-screen px-3 sm:px-4 pt-4 sm:pt-16 pb-20">
        <div className="fixed inset-0 bg-slate-900/50 transition-opacity" onClick={onClose} />
        
        <motion.div
          data-modal="true"
          initial={{ opacity: 0, scale: 0.95, y: 20 }}
  animate={{ opacity: 1, scale: 1, y: 0 }}
  exit={{ opacity: 0, scale: 0.95, y: 20 }}
  className="relative bg-white rounded-xl sm:rounded-2xl shadow-xl max-w-3xl w-full mx-auto overflow-hidden"
>
          {/* Header */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-slate-100 flex items-center justify-between bg-slate-50">
            <div className="min-w-0 flex-1">
              <h3 className="text-base sm:text-lg font-semibold text-slate-900">Customize Agent</h3>
              <p className="text-xs sm:text-sm text-slate-500 truncate">{campaign?.name}</p>
            </div>
            <button onClick={onClose} className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg ml-2">
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Content */}
          <div className="px-4 sm:px-6 py-4 sm:py-5 max-h-[60vh] sm:max-h-[65vh] overflow-y-auto space-y-5 sm:space-y-6">
            
            {/* Unsaved Warning */}
            {unsavedChanges && (
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-2.5 sm:p-3 flex items-center space-x-2">
                <AlertCircle className="w-4 h-4 text-amber-600 flex-shrink-0" />
                <span className="text-xs sm:text-sm text-amber-800">You have unsaved changes</span>
              </div>
            )}

            {/* AI Identity */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <User className="w-4 h-4 text-orange-500" />
                <h4 className="font-semibold text-slate-800 text-sm sm:text-base">AI Identity</h4>
              </div>
              <div>
                <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Representative Name</label>
                <input
                  type="text"
                  value={strategyConfig.businessName}
                  onChange={(e) => onStrategyChange('businessName', e.target.value)}
                  disabled={!canEditInstructions}
                  placeholder="Alex"
                  className="w-full sm:max-w-xs px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                />
              </div>
            </div>

            {/* Playbooks */}
            {strategyTemplates && strategyTemplates.length > 0 && (
              <div>
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center space-x-2">
                    <Sparkles className="w-4 h-4 text-orange-500" />
                    <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Playbooks</h4>
                  </div>
                  <span className="text-[10px] sm:text-xs text-slate-400">Apply proven patterns</span>
                </div>
                <div className="flex flex-wrap gap-2">
                  {strategyTemplates.map((template, idx) => (
                    <button
                      key={idx}
                      onClick={() => onApplyTemplate(template)}
                      disabled={!canEditInstructions}
                      className="px-2.5 sm:px-3 py-1.5 bg-slate-100 hover:bg-orange-50 text-slate-700 hover:text-orange-600 text-xs sm:text-sm rounded-lg transition-colors disabled:opacity-50"
                    >
                      {template.name}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Initial Contact */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <MessageSquare className="w-4 h-4 text-orange-500" />
                <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Initial Contact</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Tone</label>
                  <select
                    value={strategyConfig.initialTone}
                    onChange={(e) => onStrategyChange('initialTone', e.target.value)}
                    disabled={!canEditInstructions}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                  >
                    <option value="">Select tone...</option>
                    {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Persona</label>
                  <select
                    value={strategyConfig.initialPersona}
                    onChange={(e) => onStrategyChange('initialPersona', e.target.value)}
                    disabled={!canEditInstructions}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                  >
                    <option value="">Select persona...</option>
                    {initialPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            </div>

            {/* Engagement Phase */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Target className="w-4 h-4 text-orange-500" />
                <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Engagement Phase</h4>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Tone</label>
                  <select
                    value={strategyConfig.engagementTone}
                    onChange={(e) => onStrategyChange('engagementTone', e.target.value)}
                    disabled={!canEditInstructions}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                  >
                    <option value="">Select tone...</option>
                    {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Persona</label>
                  <select
                    value={strategyConfig.engagementPersona}
                    onChange={(e) => onStrategyChange('engagementPersona', e.target.value)}
                    disabled={!canEditInstructions}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                  >
                    <option value="">Select persona...</option>
                    {engagementPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                  </select>
                </div>
              </div>
            </div>
{/* Goal */}
            <div>
              <div className="flex items-center space-x-2 mb-3">
                <Flag className="w-4 h-4 text-orange-500" />
                <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Goal</h4>
              </div>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Primary Goal</label>
                  <select
                    value={strategyConfig.goal}
                    onChange={(e) => onStrategyChange('goal', e.target.value)}
                    disabled={!canEditInstructions}
                    className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                  >
                    <option value="qualify_handoff">Qualify and Hand Off</option>
                    <option value="book_demo">Book a Demo</option>
                    <option value="schedule_call">Schedule a Phone Call</option>
                  </select>
                </div>
                {(strategyConfig.goal === 'book_demo' || strategyConfig.goal === 'schedule_call') && (
                  <>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Booking URL (Calendly, HubSpot, Cal.com, or any scheduling link)</label>
                      <input
                        type="text"
                        value={strategyConfig.bookingUrl}
                        onChange={(e) => onStrategyChange('bookingUrl', e.target.value)}
                        disabled={!canEditInstructions}
                        placeholder="https://calendly.com/your-link"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                      />
                    </div>
                    <div>
                      <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-1">Custom booking prompt (optional)</label>
                      <input
                        type="text"
                        value={strategyConfig.bookingPrompt}
                        onChange={(e) => onStrategyChange('bookingPrompt', e.target.value)}
                        disabled={!canEditInstructions}
                        placeholder="e.g. Would you be open to a quick 20-minute Zoom?"
                        className="w-full px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-sm"
                      />
                      <p className="text-xs text-slate-400 mt-1">If blank, Infinity uses its own judgment on when and how to push.</p>
                    </div>
                  </>
                )}
              </div>
            </div>

            {/* Path 1: No-Reply Follow-up */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <h4 className="font-semibold text-slate-800 text-sm sm:text-base">No-Reply Follow-up</h4>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600">
                    {strategyConfig.path1Enabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <div
                    onClick={() => canEditInstructions && onStrategyChange('path1Enabled', !strategyConfig.path1Enabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${strategyConfig.path1Enabled ? 'bg-orange-500' : 'bg-slate-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${strategyConfig.path1Enabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs text-slate-500 mb-2">For leads who never respond (1 follow-up, then marked dead)</p>
                {strategyConfig.path1Enabled && (
                  <div className="max-w-[120px] mt-2">
                    <label className="block text-[10px] sm:text-xs font-medium text-slate-600 mb-1">Days before follow-up</label>
                    <input
                      type="number"
                      value={strategyConfig.path1FollowupDelay || 3}
                      onChange={(e) => onStrategyChange('path1FollowupDelay', parseInt(e.target.value) || 3)}
                      disabled={!canEditInstructions}
                      min="1"
                      max="30"
                      className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-100 text-xs sm:text-sm"
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Path 2: AI-Scheduled Follow-up */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <h4 className="font-semibold text-slate-800 text-sm sm:text-base">AI-Scheduled Follow-up</h4>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600">
                    {strategyConfig.path2AiEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <div
                    onClick={() => canEditInstructions && onStrategyChange('path2AiEnabled', !strategyConfig.path2AiEnabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${strategyConfig.path2AiEnabled ? 'bg-orange-500' : 'bg-slate-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${strategyConfig.path2AiEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 sm:p-4">
                <p className="text-xs text-slate-500">When a lead says "reach me next month", the AI honors that timing and follows up on the requested date.</p>
              </div>
            </div>

            {/* Follow-up Sequence */}
            <div>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center space-x-2">
                  <Clock className="w-4 h-4 text-orange-500" />
                  <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Follow-up Sequence</h4>
                </div>
                <label className="flex items-center gap-1.5 cursor-pointer select-none">
                  <span className="text-[10px] sm:text-xs font-medium text-slate-600">
                    {strategyConfig.path2SequenceEnabled ? 'Enabled' : 'Disabled'}
                  </span>
                  <div
                    onClick={() => canEditInstructions && onStrategyChange('path2SequenceEnabled', !strategyConfig.path2SequenceEnabled)}
                    className={`relative w-8 h-4 rounded-full transition-colors ${strategyConfig.path2SequenceEnabled ? 'bg-orange-500' : 'bg-slate-300'} ${canEditInstructions ? 'cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                  >
                    <div className={`absolute top-0.5 w-3 h-3 rounded-full bg-white shadow transition-transform ${strategyConfig.path2SequenceEnabled ? 'translate-x-4' : 'translate-x-0.5'}`} />
                  </div>
                </label>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 sm:p-4 mb-3">
                <p className="text-xs text-slate-500">For leads who engaged with the AI but then went silent. The sequence sends timed follow-ups to bring them back into the conversation before marking them as dead.</p>
              </div>
              <div className={`space-y-3 ${!strategyConfig.path2SequenceEnabled ? 'opacity-50 pointer-events-none' : ''}`}>
                {strategyConfig.followups.map((followup, idx) => (
                  <div key={followup.id} className="bg-slate-50 rounded-lg p-3 sm:p-4">
                    <div className="flex items-center space-x-2 mb-3">
                      <div className={`w-5 h-5 sm:w-6 sm:h-6 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-white ${
                        idx === 0 ? 'bg-orange-500' : idx === 1 ? 'bg-orange-400' : 'bg-amber-400'
                      }`}>{idx + 1}</div>
                      <span className="font-medium text-slate-700 text-sm">Follow-up {idx + 1}</span>
                    </div>
                    <div className="grid grid-cols-3 gap-2 sm:gap-3">
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-slate-600 mb-1">Days</label>
                        <input
                          type="number"
                          value={followup.day}
                          onChange={(e) => onFollowupChange(idx, 'day', parseInt(e.target.value) || 1)}
                          disabled={!canEditInstructions}
                          min="1"
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-100 text-xs sm:text-sm"
                        />
                      </div>
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-slate-600 mb-1">Tone</label>
                        <select
                          value={followup.tone}
                          onChange={(e) => onFollowupChange(idx, 'tone', e.target.value)}
                          disabled={!canEditInstructions}
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-100 text-xs sm:text-sm"
                        >
                          <option value="">Same</option>
                          {toneOptions.map(t => <option key={t} value={t}>{t}</option>)}
                        </select>
                      </div>
                      <div>
                        <label className="block text-[10px] sm:text-xs font-medium text-slate-600 mb-1">Persona</label>
                        <select
                          value={followup.persona}
                          onChange={(e) => onFollowupChange(idx, 'persona', e.target.value)}
                          disabled={!canEditInstructions}
                          className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-100 text-xs sm:text-sm"
                        >
                          <option value="">Select...</option>
                          {followupPersonaOptions.map(p => <option key={p} value={p}>{p}</option>)}
                        </select>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Qualification Questions */}
            <div>
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <div className="flex items-center space-x-2">
                  <Target className="w-4 h-4 text-orange-500" />
                  <h4 className="font-semibold text-slate-800 text-sm sm:text-base">Qualification Questions</h4>
                </div>
                <label className="flex items-center space-x-2">
                  <span className="text-xs sm:text-sm text-slate-600">Require before transfer</span>
                  <div className="relative">
                    <input
                      type="checkbox"
                      checked={strategyConfig.requireQualification}
                      onChange={(e) => onStrategyChange('requireQualification', e.target.checked)}
                      disabled={!canEditInstructions}
                      className="sr-only peer"
                    />
                    <div className="w-9 sm:w-11 h-5 sm:h-6 bg-slate-200 peer-focus:ring-4 peer-focus:ring-orange-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 sm:after:h-5 after:w-4 sm:after:w-5 after:transition-all peer-checked:bg-orange-500"></div>
                  </div>
                </label>
              </div>
              
              {strategyConfig.requireQualification && (
                <div className="space-y-2 sm:space-y-3">
                  {strategyConfig.qualificationQuestions.map((q, idx) => (
                    <div key={idx} className="flex items-center space-x-2">
                      <input
                        type="text"
                        value={q}
                        onChange={(e) => onUpdateQuestion(idx, e.target.value)}
                        disabled={!canEditInstructions}
                        placeholder={`Question ${idx + 1}...`}
                        className="flex-1 px-3 py-2 border border-slate-300 rounded-lg focus:ring-2 focus:ring-orange-200 focus:border-orange-300 disabled:bg-slate-50 text-xs sm:text-sm"
                      />
                      <button
                        onClick={() => onRemoveQuestion(idx)}
                        disabled={!canEditInstructions}
                        className="p-2 text-slate-400 hover:text-red-500 transition-colors"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  ))}
                  <button
                    onClick={onAddQuestion}
                    disabled={!canEditInstructions}
                    className="flex items-center space-x-2 text-xs sm:text-sm text-orange-600 hover:text-orange-700"
                  >
                    <Plus className="w-4 h-4" />
                    <span>Add Question</span>
                  </button>
                </div>
              )}
            </div>

          </div>

          {/* Footer */}
          <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-slate-100 bg-slate-50 flex items-center justify-end space-x-3">
            <button onClick={onClose} className="px-3 sm:px-4 py-2 text-slate-600 hover:text-slate-800 font-medium text-sm">
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || !canRebuildBundle || !unsavedChanges}
              className="flex items-center space-x-2 px-4 sm:px-6 py-2 bg-orange-500 text-white font-medium rounded-lg hover:bg-orange-600 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Saving...</span>
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  <span>Save & Rebuild</span>
                </>
              )}
            </button>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

// ============================================
// MAIN AI STUDIO
// ============================================
const AIStudio = () => {
  const { user, hasPermission } = useAuth();
  // /ai-studio?tab=ai-learning opens on that tab (the demo guide uses it); anything else opens on Knowledge Base as before.
  const [activeTab, setActiveTab] = useState(() => {
    try { const t = new URLSearchParams(window.location.search).get('tab'); return ['knowledge-base', 'ai-behavior', 'ab-testing', 'ai-learning'].includes(t) ? t : 'knowledge-base'; } catch { return 'knowledge-base'; }
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const canViewAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS) || hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW);
  const canEditAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);
  const canEditInstructions = hasPermission(PERMISSIONS.EDIT_TONE_PERSONA_INDUSTRY_INSTRUCTIONS);
  const canRebuildBundle = hasPermission(PERMISSIONS.REBUILD_AI_INSTRUCTION_BUNDLE);
  const canViewKnowledgeBase = hasPermission(PERMISSIONS.VIEW_AI_BUNDLE_PREVIEW) || hasPermission(PERMISSIONS.UPLOAD_KNOWLEDGE_BASE_DOCS);

  const [campaigns, setCampaigns] = useState([]);
  const [expandedCampaign, setExpandedCampaign] = useState(null);
  const [customizingCampaign, setCustomizingCampaign] = useState(null);
  const [unsavedChanges, setUnsavedChanges] = useState(false);
  const [saving, setSaving] = useState(false);
  const [companyIndustry, setCompanyIndustry] = useState('');

const [strategyConfig, setStrategyConfig] = useState({
    businessName: '',
    initialTone: '',
    initialPersona: '',
    engagementTone: '',
    engagementPersona: '',
    requireQualification: false,
    qualificationQuestions: [],
    goal: 'qualify_and_hand_off',
    bookingUrl: '',
    bookingPrompt: '',
    path1FollowupDelay: 3,
    path1Enabled: true,
    path2AiEnabled: true,
    path2SequenceEnabled: true,
    followups: [
      { id: 1, day: 3, tone: '', persona: '' },
      { id: 2, day: 7, tone: '', persona: '' },
      { id: 3, day: 14, tone: '', persona: '' }
    ]
  });

  const isGlobalAdmin = user?.role === 'global_admin';

  // Listen for tour tab switch requests
useEffect(() => {
  const handleTourTabSwitch = (event) => {
    const { tabId } = event.detail;
    console.log('🎯 AI Studio: Tour requested tab switch:', tabId);
    setActiveTab(tabId);
  };
  
  window.addEventListener('tour-switch-tab', handleTourTabSwitch);
  return () => window.removeEventListener('tour-switch-tab', handleTourTabSwitch);
}, []);

  // Load campaigns
  useEffect(() => {
    const fetchCampaigns = async () => {
      if (!user?.tenant_id || !canViewAISettings) { setLoading(false); return; }
      try {
        const data = await callAPI(`/campaigns?tenant_id=${user.tenant_id}`);
        setCampaigns(data || []);
        const { data: industryData } = await supabase
          .from('platform_settings')
          .select('value')
          .eq('tenant_id', user.tenant_id)
          .eq('key', 'industry')
          .single();
        setCompanyIndustry(industryData?.value || '');
      } catch (err) {
        setError(`Failed to load: ${err.message}`);
      } finally {
        setLoading(false);
      }
    };
    fetchCampaigns();
  }, [user?.tenant_id, canViewAISettings]);

  // Load campaign config when customizing
  useEffect(() => {
    const loadConfig = async () => {
      if (!customizingCampaign?.id) return;
      try {
const { data, error } = await supabase
          .from('campaigns')
          .select(`
            initial_tone, initial_persona, engagement_tone, engagement_persona,
            path1_followup_delay, followup_delay_1, followup_delay_2, followup_delay_3,
            followup_1_tone, followup_1_persona, followup_2_tone, followup_2_persona,
            followup_3_tone, followup_3_persona, ai_representative_name,
            require_qualification_before_transfer, qualification_questions,
            goal, booking_url, booking_prompt
          `)
          .eq('id', customizingCampaign.id)
          .single();
        if (error) throw error;
setStrategyConfig({
          businessName: data.ai_representative_name || 'Alex',
          initialTone: data.initial_tone || '',
          initialPersona: data.initial_persona || '',
          engagementTone: data.engagement_tone || '',
          engagementPersona: data.engagement_persona || '',
          goal: data.goal || 'qualify_and_hand_off',
          bookingUrl: data.booking_url || '',
          bookingPrompt: data.booking_prompt || '',
          requireQualification: data.require_qualification_before_transfer || false,
          qualificationQuestions: data.qualification_questions || [],
          path1FollowupDelay: data.path1_followup_delay || 3,
          path1Enabled: data.path1_followup_delay !== 0,
          path2AiEnabled: data.path2_ai_followup_enabled !== false,
          path2SequenceEnabled: !(data.followup_delay_1 === 0 && data.followup_delay_2 === 0 && data.followup_delay_3 === 0),
          followups: [
            { id: 1, day: data.followup_delay_1 || 3, tone: data.followup_1_tone || '', persona: data.followup_1_persona || '' },
            { id: 2, day: data.followup_delay_2 || 7, tone: data.followup_2_tone || '', persona: data.followup_2_persona || '' },
            { id: 3, day: data.followup_delay_3 || 14, tone: data.followup_3_tone || '', persona: data.followup_3_persona || '' }
          ]
        });
        setUnsavedChanges(false);
      } catch (err) { console.error(err); }
    };
    loadConfig();
  }, [customizingCampaign?.id]);

  // Handlers
  const handleChange = useCallback((key, value) => {
    if (!canEditInstructions) return;
    setStrategyConfig(prev => ({ ...prev, [key]: value }));
    setUnsavedChanges(true);
  }, [canEditInstructions]);

  const handleFollowupChange = useCallback((index, field, value) => {
    if (!canEditInstructions) return;
    setStrategyConfig(prev => ({
      ...prev,
      followups: prev.followups.map((f, i) => i === index ? { ...f, [field]: value } : f)
    }));
    setUnsavedChanges(true);
  }, [canEditInstructions]);

  const addQuestion = useCallback(() => {
    setStrategyConfig(prev => ({ ...prev, qualificationQuestions: [...prev.qualificationQuestions, ''] }));
    setUnsavedChanges(true);
  }, []);

  const updateQuestion = useCallback((index, value) => {
    setStrategyConfig(prev => {
      const updated = [...prev.qualificationQuestions];
      updated[index] = value;
      return { ...prev, qualificationQuestions: updated };
    });
    setUnsavedChanges(true);
  }, []);

  const removeQuestion = useCallback((index) => {
    setStrategyConfig(prev => ({
      ...prev,
      qualificationQuestions: prev.qualificationQuestions.filter((_, i) => i !== index)
    }));
    setUnsavedChanges(true);
  }, []);

  const applyTemplate = useCallback((template) => {
    if (!canEditInstructions) return;
    setStrategyConfig(prev => ({
      ...prev,
      initialTone: template.tone,
      initialPersona: initialPersonaOptions[0],
      engagementTone: template.tone,
      engagementPersona: template.persona,
      followups: prev.followups.map((f, i) => ({
        ...f,
        tone: template.tone,
        persona: followupPersonaOptions[i] || 'Gentle Reminder'
      }))
    }));
    setUnsavedChanges(true);
  }, [canEditInstructions]);

  // Save
  const saveStrategy = async () => {
    if (!canRebuildBundle || !customizingCampaign) return;
    setSaving(true);
    setError('');
    try {
      const campaignMetadata = {
        campaign_id: customizingCampaign.id,
        campaign_name: customizingCampaign.name,
        talk_track_type: customizingCampaign.talk_track_type,
        talk_track_specialty: customizingCampaign.talk_track_specialty
      };

      let tenantBehaviors = {};
      const { data: behaviorSettings } = await supabase
        .from('tenant_behavior_settings')
        .select('*')
        .eq('tenant_id', user.tenant_id)
        .single();

      if (behaviorSettings) {
        tenantBehaviors = {
          push_back_on_not_interested: behaviorSettings.push_back_on_not_interested,
          challenge_budget_objections: behaviorSettings.challenge_budget_objections,
          gate_information: behaviorSettings.gate_information
        };
      }

      const platformSettings = { ai_representative_name: { value: strategyConfig.businessName } };

      const initialBundle = buildInitialInstruction({
        tone: strategyConfig.initialTone,
        persona: strategyConfig.initialPersona,
        industry: companyIndustry,
        campaignMetadata,
        platformSettings,
        tenantBehaviors,
        requireQualification: strategyConfig.requireQualification,
        qualificationQuestions: strategyConfig.qualificationQuestions
      });

      const engagementBundle = buildInstructionBundle({
        tone: strategyConfig.engagementTone,
        persona: strategyConfig.engagementPersona,
        industry: companyIndustry,
        campaignMetadata,
        platformSettings,
        tenantBehaviors,
        requireQualification: strategyConfig.requireQualification,
        qualificationQuestions: strategyConfig.qualificationQuestions
      });

      const followupBundles = strategyConfig.followups.map((f, i) =>
        buildFollowupInstruction({
          tone: f.tone || strategyConfig.engagementTone,
          persona: f.persona,
          industry: companyIndustry,
          campaignMetadata,
          followupStage: i + 1,
          platformSettings,
          tenantBehaviors,
          requireQualification: strategyConfig.requireQualification,
          qualificationQuestions: strategyConfig.qualificationQuestions
        })
      );

await supabase.from('campaigns').update({
        ai_instruction_initial: initialBundle,
        ai_instruction_engagement: engagementBundle,
        ai_instruction_followup_1: followupBundles[0],
        ai_instruction_followup_2: followupBundles[1],
        ai_instruction_followup_3: followupBundles[2],
        path1_followup_delay: strategyConfig.path1Enabled ? strategyConfig.path1FollowupDelay : 0,
        path2_ai_followup_enabled: strategyConfig.path2AiEnabled,
        initial_tone: strategyConfig.initialTone,
        initial_persona: strategyConfig.initialPersona,
        engagement_tone: strategyConfig.engagementTone,
        engagement_persona: strategyConfig.engagementPersona,
        goal: strategyConfig.goal,
        booking_url: strategyConfig.bookingUrl || null,
        booking_prompt: strategyConfig.bookingPrompt || null,
        require_qualification_before_transfer: strategyConfig.requireQualification,
        qualification_questions: strategyConfig.qualificationQuestions,
        followup_delay_1: strategyConfig.path2SequenceEnabled ? strategyConfig.followups[0]?.day : 0,
        followup_delay_2: strategyConfig.path2SequenceEnabled ? strategyConfig.followups[1]?.day : 0,
        followup_delay_3: strategyConfig.path2SequenceEnabled ? strategyConfig.followups[2]?.day : 0,
        followup_1_tone: strategyConfig.followups[0]?.tone,
        followup_1_persona: strategyConfig.followups[0]?.persona,
        followup_2_tone: strategyConfig.followups[1]?.tone,
        followup_2_persona: strategyConfig.followups[1]?.persona,
        followup_3_tone: strategyConfig.followups[2]?.tone,
        followup_3_persona: strategyConfig.followups[2]?.persona,
        ai_representative_name: strategyConfig.businessName
      }).eq('id', customizingCampaign.id);

      setCampaigns(prev => prev.map(c =>
        c.id === customizingCampaign.id
          ? { 
              ...c, 
              initial_tone: strategyConfig.initialTone,
              engagement_tone: strategyConfig.engagementTone, 
              engagement_persona: strategyConfig.engagementPersona,
              ai_representative_name: strategyConfig.businessName,
              followup_delay_1: strategyConfig.followups[0]?.day,
              followup_delay_2: strategyConfig.followups[1]?.day,
              followup_delay_3: strategyConfig.followups[2]?.day
            }
          : c
      ));

      setSuccess(`Saved "${customizingCampaign.name}"`);
      setUnsavedChanges(false);
      setCustomizingCampaign(null);
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError(`Save failed: ${err.message}`);
    } finally {
      setSaving(false);
    }
  };

  // Loading
  if (loading) {
    return (
      <div className="min-h-[50vh] sm:min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="w-8 h-8 sm:w-10 sm:h-10 border-2 sm:border-3 border-slate-200 border-t-orange-500 rounded-full animate-spin" />
      </div>
    );
  }

  if (!canViewAISettings && !canViewKnowledgeBase) {
    return (
      <div className="min-h-[50vh] sm:min-h-screen bg-slate-50 flex items-center justify-center px-4">
        <div className="text-center">
          <AlertCircle className="w-10 h-10 sm:w-12 sm:h-12 text-red-500 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-slate-900">Access Restricted</h3>
        </div>
      </div>
    );
  }

  // Render agents
  const renderAgentList = () => (
    <div className="space-y-4">
      {campaigns.length === 0 ? (
        <div className="bg-white rounded-xl border border-slate-200 p-8 sm:p-12 text-center">
          <Bot className="w-10 h-10 sm:w-12 sm:h-12 text-slate-300 mx-auto mb-4" />
          <h3 className="text-base sm:text-lg font-semibold text-slate-900 mb-2">No AI agents yet</h3>
          <p className="text-slate-500 text-xs sm:text-sm">Create a campaign to get started.</p>
        </div>
      ) : (
        <div className="space-y-3">
{campaigns.map((campaign) => (
  <AgentCard
    key={campaign.id}
    campaign={campaign}
    tenantId={user?.tenant_id}
    isExpanded={expandedCampaign?.id === campaign.id}
    onToggle={() => setExpandedCampaign(expandedCampaign?.id === campaign.id ? null : campaign)}
    onOpenCustomize={() => setCustomizingCampaign(campaign)}
  />
))}
        </div>
      )}

      {/* Customize Modal */}
      <CustomizeModal
        isOpen={!!customizingCampaign}
        onClose={() => { setCustomizingCampaign(null); setUnsavedChanges(false); }}
        campaign={customizingCampaign}
        strategyConfig={strategyConfig}
        onStrategyChange={handleChange}
        onFollowupChange={handleFollowupChange}
        onAddQuestion={addQuestion}
        onUpdateQuestion={updateQuestion}
        onRemoveQuestion={removeQuestion}
        onApplyTemplate={applyTemplate}
        onSave={saveStrategy}
        saving={saving}
        unsavedChanges={unsavedChanges}
        canEditInstructions={canEditInstructions}
        canRebuildBundle={canRebuildBundle}
      />
    </div>
  );

  // Tab definitions
  const tabs = [
    { id: 'knowledge-base', label: 'Knowledge', icon: FileText },
    { id: 'ai-behavior', label: 'Behavior', icon: Zap },
    { id: 'ab-testing', label: 'AB Testing', icon: FlaskConical },
    { id: 'ai-learning', label: 'AI Learning', icon: GraduationCap },
    ...(isGlobalAdmin ? [{ id: 'global-rebuild', label: 'Admin', icon: RefreshCw }] : [])
  ];

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto px-3 sm:px-6 py-4 sm:py-8">
        
        {/* Header */}
        <div className="mb-6 sm:mb-8 flex items-center space-x-3 sm:space-x-4">
          <div className="w-10 h-10 sm:w-12 sm:h-12 bg-orange-500 rounded-xl flex items-center justify-center flex-shrink-0">
            <Brain className="w-5 h-5 sm:w-6 sm:h-6 text-white" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-bold text-slate-900">AI Studio</h1>
            <p className="text-xs sm:text-sm text-slate-500">Your intelligent agents at work</p>
          </div>
        </div>

        {/* Messages */}
        <AnimatePresence>
          {error && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-4 sm:mb-6 bg-red-50 border border-red-200 rounded-xl p-3 sm:p-4 flex items-center justify-between">
              <div className="flex items-center space-x-2 sm:space-x-3 min-w-0">
                <AlertCircle className="w-4 h-4 sm:w-5 sm:h-5 text-red-600 flex-shrink-0" />
                <span className="text-red-800 text-xs sm:text-sm truncate">{error}</span>
              </div>
              <button onClick={() => setError('')} className="ml-2 flex-shrink-0"><X className="w-4 h-4 text-red-400" /></button>
            </motion.div>
          )}
          {success && (
            <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }}
              className="mb-4 sm:mb-6 bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:p-4 flex items-center space-x-2 sm:space-x-3">
              <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" />
              <span className="text-emerald-800 text-xs sm:text-sm">{success}</span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Tabs - Scrollable on mobile */}
        <div className="bg-white rounded-xl border border-slate-200 mb-4 sm:mb-6 overflow-hidden">
          <nav className="flex overflow-x-auto scrollbar-hide">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`tour-tab-${tab.id} flex-shrink-0 flex-1 min-w-[80px] sm:min-w-0 px-3 sm:px-4 py-2.5 sm:py-3 text-xs sm:text-sm font-medium flex items-center justify-center space-x-1.5 sm:space-x-2 transition-all relative ${
                  activeTab === tab.id ? 'text-orange-600' : 'text-slate-500 hover:text-slate-900 hover:bg-slate-50'
                }`}
              >
                <tab.icon className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                <span className="whitespace-nowrap">{tab.label}</span>
                {activeTab === tab.id && (
                  <motion.div layoutId="activeTab" className="absolute bottom-0 left-0 right-0 h-0.5 bg-orange-500" />
                )}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        {activeTab === 'knowledge-base' && <div className="tour-knowledge-base-content"><KnowledgeBase /></div>}
        {activeTab === 'ai-behavior' && <div className="tour-ai-behavior-content"><AIBehaviorTab /></div>}
        {activeTab === 'ab-testing' && <ABTestingDashboard />}
        {activeTab === 'ai-learning' && <LearningAnalytics />}
        {activeTab === 'global-rebuild' && isGlobalAdmin && <InstructionRebuildTool />}
      </div>
    </div>
  );
};

// ============================================
// AI BEHAVIOR TAB
// ============================================
const AIBehaviorTab = () => {
  const { user, hasPermission } = useAuth();
  const canEditAISettings = hasPermission(PERMISSIONS.VIEW_EDIT_AI_SETTINGS);
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  
  const [minScore, setMinScore] = useState('75');
  const [escalationMethod, setEscalationMethod] = useState('All');
  const [pushBackOnNotInterested, setPushBackOnNotInterested] = useState(false);
  const [challengeBudgetObjections, setChallengeBudgetObjections] = useState(false);
  const [gateInformation, setGateInformation] = useState(false);
  const [notificationPhone, setNotificationPhone] = useState('');
  const [phoneModalOpen, setPhoneModalOpen] = useState(false);
  const [phoneDraft, setPhoneDraft] = useState('');
  const [phoneError, setPhoneError] = useState('');
  // Method the dropdown was on before the user picked an SMS option, so cancelling
  // the modal cannot leave them on SMS with no number to text.
  const [methodBeforeModal, setMethodBeforeModal] = useState('All');

  useEffect(() => {
    const fetchSettings = async () => {
      if (!user?.tenant_id) { setLoading(false); return; }
      try {
        const settings = await callAPI(`/settings?tenant_id=${user.tenant_id}`);
        setMinScore(settings['ai_min_escalation_score']?.value || '75');
        setEscalationMethod(settings['ai_escalation_method']?.value || 'All');
        const { data: tenantRow } = await supabase.from('tenants').select('notification_phone').eq('id', user.tenant_id).single();
        setNotificationPhone(tenantRow?.notification_phone || '');
        const { data } = await supabase.from('tenant_behavior_settings').select('*').eq('tenant_id', user.tenant_id).single();
        if (data) {
          setPushBackOnNotInterested(data.push_back_on_not_interested || false);
          setChallengeBudgetObjections(data.challenge_budget_objections || false);
          setGateInformation(data.gate_information || false);
        }
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    fetchSettings();
  }, [user?.tenant_id]);

  const methodUsesSms = (m) => m === 'SMS' || m === 'All';

  // Digits only, then E.164. US numbers are assumed when no country code is given,
  // matching how lead phones are normalised elsewhere.
  const toE164 = (raw) => {
    const digits = (raw || '').replace(/\D/g, '');
    if (digits.length === 10) return `+1${digits}`;
    if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
    if (digits.length > 11 && (raw || '').trim().startsWith('+')) return `+${digits}`;
    return null;
  };

  const handleMethodChange = (nextMethod) => {
    // Picking an SMS option with no number on file has to collect one, or the
    // alert silently goes nowhere.
    if (methodUsesSms(nextMethod) && !notificationPhone) {
      setMethodBeforeModal(escalationMethod);
      setPhoneDraft('');
      setPhoneError('');
      setPhoneModalOpen(true);
    }
    setEscalationMethod(nextMethod);
  };

  const handlePhoneConfirm = async () => {
    const formatted = toE164(phoneDraft);
    if (!formatted) {
      setPhoneError('Enter a valid phone number, for example (555) 123-4567');
      return;
    }
    const { error: phoneErr } = await supabase
      .from('tenants')
      .update({ notification_phone: formatted })
      .eq('id', user.tenant_id);
    if (phoneErr) {
      setPhoneError('Could not save that number: ' + phoneErr.message);
      return;
    }
    setNotificationPhone(formatted);
    setPhoneModalOpen(false);
  };

  const handlePhoneCancel = () => {
    setPhoneModalOpen(false);
    if (!notificationPhone) setEscalationMethod(methodBeforeModal);
  };

  const handleSave = async () => {
    if (!canEditAISettings) return;
    // Guard the save too, in case the method was already SMS from a prior session.
    if (methodUsesSms(escalationMethod) && !notificationPhone) {
      setMethodBeforeModal('Email');
      setPhoneDraft('');
      setPhoneError('');
      setPhoneModalOpen(true);
      return;
    }
    setSaving(true);
    try {
      await callAPI('/settings', {
        method: 'PUT',
        body: JSON.stringify({
          settings: { ai_min_escalation_score: { value: minScore }, ai_escalation_method: { value: escalationMethod } },
          tenant_id: user.tenant_id
        })
      });
      await supabase.from('tenant_behavior_settings').upsert({
        tenant_id: user.tenant_id,
        push_back_on_not_interested: pushBackOnNotInterested,
        challenge_budget_objections: challengeBudgetObjections,
        gate_information: gateInformation
      }, { onConflict: 'tenant_id' });
      setSuccess('Saved');
      setTimeout(() => setSuccess(''), 2000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  if (loading) return <div className="flex justify-center py-12"><div className="w-8 h-8 border-2 border-orange-500/20 border-t-orange-500 rounded-full animate-spin" /></div>;

  return (
    <div className="space-y-4 sm:space-y-6">
      {success && (
        <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-3 sm:p-4 flex items-center space-x-2 sm:space-x-3">
          <CheckCircle className="w-4 h-4 sm:w-5 sm:h-5 text-emerald-600 flex-shrink-0" />
          <span className="text-emerald-800 text-xs sm:text-sm">{success}</span>
        </div>
      )}

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">Conversation Style</h3>
        <div className="space-y-2 sm:space-y-3">
          {[
            { label: 'Push back on "not interested"', desc: 'Ask "Can I ask why?" before accepting', value: pushBackOnNotInterested, set: setPushBackOnNotInterested },
            { label: 'Challenge budget objections', desc: 'Explore ROI before accepting', value: challengeBudgetObjections, set: setChallengeBudgetObjections },
            { label: 'Gate detailed information', desc: 'Qualify before sharing pricing', value: gateInformation, set: setGateInformation }
          ].map((item, i) => (
            <label key={i} className="flex items-center justify-between p-2.5 sm:p-3 bg-slate-50 rounded-lg cursor-pointer hover:bg-slate-100 gap-3">
              <div className="min-w-0 flex-1">
                <div className="font-medium text-slate-900 text-xs sm:text-sm">{item.label}</div>
                <p className="text-[10px] sm:text-sm text-slate-500">{item.desc}</p>
              </div>
              <input type="checkbox" checked={item.value} onChange={(e) => item.set(e.target.checked)} disabled={!canEditAISettings}
                className="w-4 h-4 sm:w-5 sm:h-5 text-orange-500 rounded focus:ring-orange-500 flex-shrink-0" />
            </label>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-xl border border-slate-200 p-4 sm:p-6">
        <h3 className="font-semibold text-slate-900 mb-3 sm:mb-4 text-sm sm:text-base">Escalation</h3>
        <div className="space-y-4">
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">Minimum score to escalate</label>
            <div className="flex items-center space-x-3 sm:space-x-4">
              <input type="range" min="1" max="100" value={minScore} onChange={(e) => setMinScore(e.target.value)} className="flex-1" />
              <span className="text-xl sm:text-2xl font-bold text-orange-500 w-10 sm:w-12 text-right">{minScore}</span>
            </div>
          </div>
          <div>
            <label className="block text-xs sm:text-sm font-medium text-slate-700 mb-2">Notify via</label>
            <select value={escalationMethod} onChange={(e) => handleMethodChange(e.target.value)} disabled={!canEditAISettings}
              className="w-full sm:max-w-xs px-3 sm:px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-200 text-sm">
              {['Email', 'SMS', 'Dashboard Only', 'All'].map(m => <option key={m} value={m}>{m}</option>)}
            </select>
            {methodUsesSms(escalationMethod) && notificationPhone && (
              <div className="mt-2 flex items-center gap-2 text-xs sm:text-sm text-slate-600">
                <span>Texting alerts to <span className="font-medium text-slate-900">{notificationPhone}</span></span>
                <button type="button" disabled={!canEditAISettings}
                  onClick={() => { setMethodBeforeModal(escalationMethod); setPhoneDraft(notificationPhone); setPhoneError(''); setPhoneModalOpen(true); }}
                  className="text-orange-600 hover:text-orange-700 font-medium disabled:opacity-50">Change</button>
              </div>
            )}
          </div>
        </div>
      </div>

      <div className="flex justify-end">
        <button onClick={handleSave} disabled={saving}
          className="w-full sm:w-auto px-6 py-2.5 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 disabled:opacity-50 text-sm">
          {saving ? 'Saving...' : 'Save Settings'}
        </button>
      </div>

      {phoneModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/50 p-4">
          <div className="bg-white rounded-xl border border-slate-200 shadow-xl w-full max-w-sm p-5 sm:p-6">
            <h3 className="font-semibold text-slate-900 text-sm sm:text-base">Where should we text hot lead alerts?</h3>
            <p className="text-xs sm:text-sm text-slate-500 mt-1">This number receives a text the moment a lead goes hot.</p>
            <input
              type="tel"
              autoFocus
              value={phoneDraft}
              onChange={(e) => { setPhoneDraft(e.target.value); setPhoneError(''); }}
              onKeyDown={(e) => { if (e.key === 'Enter') handlePhoneConfirm(); }}
              placeholder="(555) 123-4567"
              className="mt-4 w-full px-3 sm:px-4 py-2 border border-slate-300 rounded-xl focus:ring-2 focus:ring-orange-200 text-sm"
            />
            {phoneError && <p className="mt-2 text-xs sm:text-sm text-red-600">{phoneError}</p>}
            <div className="mt-5 flex justify-end gap-2">
              <button type="button" onClick={handlePhoneCancel}
                className="px-4 py-2 text-slate-600 hover:text-slate-900 font-medium rounded-xl text-sm">Cancel</button>
              <button type="button" onClick={handlePhoneConfirm}
                className="px-5 py-2 bg-orange-500 text-white font-medium rounded-xl hover:bg-orange-600 text-sm">Save Number</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AIStudio;